import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Database, Plus, Upload, Pencil, Trash, Eye, EyeSlash, Lock, LockOpen, Users, ArrowUp, ArrowDown
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'date', label: 'Date' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'comments', label: 'Comments / Remarks' },
  { value: 'radio', label: 'Radio (Single Choice)' },
  { value: 'multi_select', label: 'Multi-Select (Checkboxes)' },
];

const DataStructuresPage = () => {
  const [structures, setStructures] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isFieldDialogOpen, setIsFieldDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', branch_field_mapping: '', branch_access: [], user_access: [] });
  const [editFields, setEditFields] = useState([]);
  const [newField, setNewField] = useState({ name: '', type: 'text', required: false, visible: true, editable: true, options: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [sRes, bRes, uRes] = await Promise.all([
        axios.get(`${API_URL}/api/data-structures`),
        axios.get(`${API_URL}/api/branches`),
        axios.get(`${API_URL}/api/users`)
      ]);
      setStructures(sRes.data);
      setBranches(bRes.data);
      setUsers(uRes.data);
    } catch (e) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadName) { toast.error('Please provide a name and file'); return; }
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('name', uploadName);
    formData.append('description', uploadDescription);
    try {
      await axios.post(`${API_URL}/api/data-structures/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Schema created from file');
      setIsUploadDialogOpen(false);
      setUploadFile(null); setUploadName(''); setUploadDescription('');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create');
    }
  };

  const openEditDialog = (structure) => {
    setSelectedStructure(structure);
    setEditForm({
      name: structure.name,
      description: structure.description || '',
      branch_field_mapping: structure.branch_field_mapping || '',
      branch_access: structure.branch_access || [],
      user_access: structure.user_access || []
    });
    setEditFields((structure.fields || []).map(f => ({
      name: f.name,
      type: f.type || f.field_type || 'text',
      required: f.required || false,
      visible: f.visible !== false,
      editable: f.editable !== false,
      options: (f.options || []).join(', ')
    })));
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const fields = editFields.map(f => ({
        name: f.name,
        type: f.type,
        required: f.required,
        visible: f.visible,
        editable: f.editable,
        options: (f.type === 'radio' || f.type === 'multi_select') ? f.options.split(',').map(o => o.trim()).filter(Boolean) : []
      }));
      await axios.put(`${API_URL}/api/data-structures/${selectedStructure.id}`, {
        name: editForm.name,
        description: editForm.description,
        fields,
        branch_field_mapping: editForm.branch_field_mapping || null,
        branch_access: editForm.branch_access,
        user_access: editForm.user_access
      });
      toast.success('Schema updated');
      setIsEditDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this schema? All records will remain but become unlinked.')) return;
    try {
      await axios.delete(`${API_URL}/api/data-structures/${id}`);
      toast.success('Schema deleted');
      fetchData();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const toggleBranchAccess = (branchId) => {
    setEditForm(prev => ({
      ...prev,
      branch_access: prev.branch_access.includes(branchId)
        ? prev.branch_access.filter(id => id !== branchId)
        : [...prev.branch_access, branchId]
    }));
  };

  const toggleUserAccess = (userId) => {
    setEditForm(prev => ({
      ...prev,
      user_access: prev.user_access.includes(userId)
        ? prev.user_access.filter(id => id !== userId)
        : [...prev.user_access, userId]
    }));
  };

  const updateField = (idx, key, value) => {
    setEditFields(prev => prev.map((f, i) => i === idx ? { ...f, [key]: value } : f));
  };

  const moveField = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= editFields.length) return;
    const arr = [...editFields];
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setEditFields(arr);
  };

  const removeField = (idx) => {
    setEditFields(prev => prev.filter((_, i) => i !== idx));
  };

  const addField = () => {
    if (!newField.name.trim()) { toast.error('Field name required'); return; }
    setEditFields(prev => [...prev, { ...newField, options: newField.options || '', source: 'manual' }]);
    setNewField({ name: '', type: 'text', required: false, visible: true, editable: true, options: '' });
    setIsFieldDialogOpen(false);
  };

  if (loading) {
    return <div className="space-y-6"><div className="h-8 w-48 skeleton"></div><div className="h-64 skeleton rounded-md"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="data-structures-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Database size={28} weight="duotone" className="text-primary" /> Data Schemas</h1>
          <p className="text-muted-foreground">Define and manage record schemas for different policy types</p>
        </div>
        <Button onClick={() => setIsUploadDialogOpen(true)} data-testid="upload-schema-btn">
          <Upload size={18} className="mr-2" /> Upload Schema
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {structures.map(s => (
          <Card key={s.id} className="card-hover" data-testid={`schema-card-${s.id}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-base">{s.name}</CardTitle>
                  {s.description && <CardDescription className="text-xs mt-1">{s.description}</CardDescription>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDialog(s)} data-testid={`edit-schema-${s.id}`}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(s.id)}>
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-wrap gap-1.5 mb-3">
                {(s.fields || []).slice(0, 6).map(f => (
                  <Badge key={f.name} variant={f.visible === false ? 'outline' : 'secondary'} className="text-xs">
                    {f.visible === false && <EyeSlash size={10} className="mr-0.5" />}
                    {f.required && <span className="text-destructive mr-0.5">*</span>}
                    {f.name}
                  </Badge>
                ))}
                {(s.fields || []).length > 6 && <Badge variant="outline" className="text-xs">+{s.fields.length - 6} more</Badge>}
              </div>
              <div className="flex gap-2 text-xs text-muted-foreground">
                {s.branch_field_mapping && <span>Branch: <strong>{s.branch_field_mapping}</strong></span>}
                {(s.user_access || []).length > 0 && <span className="flex items-center gap-0.5"><Users size={12} /> {s.user_access.length} users</span>}
              </div>
            </CardContent>
          </Card>
        ))}
        {structures.length === 0 && (
          <Card className="col-span-full"><CardContent className="py-12 text-center text-muted-foreground">
            <Database size={32} className="mx-auto mb-2 opacity-50" /><p>No schemas yet. Upload a CSV/Excel to get started.</p>
          </CardContent></Card>
        )}
      </div>

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent><DialogHeader>
          <DialogTitle>Upload Schema</DialogTitle>
          <DialogDescription>Upload a CSV or Excel file to auto-detect field structure</DialogDescription>
        </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2"><Label>Schema Name</Label><Input value={uploadName} onChange={(e) => setUploadName(e.target.value)} placeholder="e.g., Motor Insurance" required data-testid="schema-name-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={uploadDescription} onChange={(e) => setUploadDescription(e.target.value)} placeholder="Optional description" /></div>
            <div className="space-y-2"><Label>File (CSV/Excel)</Label><Input type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setUploadFile(e.target.files[0])} required data-testid="schema-file-input" /></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setIsUploadDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="schema-upload-submit">Upload</Button></div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Structure Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Schema: {editForm.name}</DialogTitle>
            <DialogDescription>Configure fields, visibility, types, and access control</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Name</Label><Input value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} required /></div>
              <div className="space-y-2"><Label>Branch Field Mapping</Label>
                <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm" value={editForm.branch_field_mapping} onChange={(e) => setEditForm({...editForm, branch_field_mapping: e.target.value})}>
                  <option value="">None</option>
                  {editFields.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Input value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} /></div>

            {/* Fields Management */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Fields ({editFields.length})</Label>
                <Button type="button" size="sm" variant="outline" onClick={() => setIsFieldDialogOpen(true)} data-testid="add-field-btn">
                  <Plus size={14} className="mr-1" /> Add Field
                </Button>
              </div>
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-28">Type</TableHead>
                      <TableHead className="w-20 text-center">Visible</TableHead>
                      <TableHead className="w-20 text-center">Editable</TableHead>
                      <TableHead className="w-20 text-center">Required</TableHead>
                      <TableHead className="w-32">Options</TableHead>
                      <TableHead className="w-16"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {editFields.map((field, idx) => (
                      <TableRow key={idx} className={!field.visible ? 'opacity-60' : ''}>
                        <TableCell className="p-1">
                          <div className="flex flex-col">
                            <button type="button" onClick={() => moveField(idx, -1)} disabled={idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ArrowUp size={12} /></button>
                            <button type="button" onClick={() => moveField(idx, 1)} disabled={idx === editFields.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ArrowDown size={12} /></button>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-sm">{field.name}</TableCell>
                        <TableCell>
                          <select className="text-xs border rounded px-1 py-0.5 bg-transparent w-full" value={field.type} onChange={(e) => updateField(idx, 'type', e.target.value)}>
                            {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox checked={field.visible} onCheckedChange={(v) => updateField(idx, 'visible', v)} />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox checked={field.editable} onCheckedChange={(v) => updateField(idx, 'editable', v)} />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox checked={field.required} onCheckedChange={(v) => updateField(idx, 'required', v)} />
                        </TableCell>
                        <TableCell>
                          {(field.type === 'radio' || field.type === 'multi_select') && (
                            <Input className="text-xs h-7" value={field.options} onChange={(e) => updateField(idx, 'options', e.target.value)} placeholder="opt1, opt2" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeField(idx)}>
                            <Trash size={12} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Branch Access */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Branch Access <span className="text-xs text-muted-foreground font-normal">(empty = all branches)</span></Label>
              <div className="flex flex-wrap gap-2">
                {branches.map(b => (
                  <button key={b.id} type="button" onClick={() => toggleBranchAccess(b.id)}
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${editForm.branch_access.includes(b.id) ? 'bg-primary text-primary-foreground border-primary' : 'border-input hover:bg-secondary'}`}>
                    {b.name}
                  </button>
                ))}
              </div>
            </div>

            {/* User Access */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">User Access <span className="text-xs text-muted-foreground font-normal">(empty = all users can view)</span></Label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {users.filter(u => u.role !== 'super_admin').map(u => (
                  <button key={u.id} type="button" onClick={() => toggleUserAccess(u.id)}
                    className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${editForm.user_access.includes(u.id) ? 'bg-primary text-primary-foreground border-primary' : 'border-input hover:bg-secondary'}`}>
                    {u.name} <span className="opacity-60">({u.role.replace('_', ' ')})</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="save-schema-btn">Save Changes</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Field Dialog */}
      <Dialog open={isFieldDialogOpen} onOpenChange={setIsFieldDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New Field</DialogTitle><DialogDescription>Add a custom field to this schema</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Field Name</Label><Input value={newField.name} onChange={(e) => setNewField({...newField, name: e.target.value})} placeholder="e.g., follow_up_date" data-testid="new-field-name" /></div>
            <div className="space-y-2"><Label>Type</Label>
              <select className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm" value={newField.type} onChange={(e) => setNewField({...newField, type: e.target.value})} data-testid="new-field-type">
                {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            {(newField.type === 'radio' || newField.type === 'multi_select') && (
              <div className="space-y-2"><Label>Options (comma-separated)</Label><Input value={newField.options} onChange={(e) => setNewField({...newField, options: e.target.value})} placeholder="Option A, Option B, Option C" /></div>
            )}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={newField.visible} onCheckedChange={(v) => setNewField({...newField, visible: v})} /> Visible</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={newField.editable} onCheckedChange={(v) => setNewField({...newField, editable: v})} /> Editable</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={newField.required} onCheckedChange={(v) => setNewField({...newField, required: v})} /> Required</label>
            </div>
            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setIsFieldDialogOpen(false)}>Cancel</Button><Button onClick={addField} data-testid="confirm-add-field">Add Field</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DataStructuresPage;
